import dayjs from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { RC_DURATION } from '@lambda/reebelo';
import { isHolidayReturnEligible } from '@lambda/commons/helpers';
import settings from '@/settings';
import { ShipmentLineItem, Shipment } from './types';
import { getWarrantyCoverageTerms, getWarrantyCoverageVersion } from './helper';

dayjs.extend(tz);
dayjs.extend(utc);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
const { free_return_days, store } = settings;

export const getDeliveryDateFromShipment = (
  shipment: Shipment | undefined | null,
) => {
  if (shipment === undefined) {
    return null;
  }
  const dateFormat = shipment?.tracking ? 'Do MMMM YYYY' : 'Do MMM YYYY';

  const deliveredDate = shipment?.deliveredBy
    ? dayjs(shipment?.deliveredBy, dateFormat).format()
    : null;

  return deliveredDate;
};

export const isWithinFreeReturnPeriod = (
  coverageStartDate: string | null | undefined,
  storeTimeZone: string,
  processedAt?: string | null | undefined,
) => {
  const dayEligibleForReturn = free_return_days;

  // No coverageStartDate means purchase was recent, return true
  if (!coverageStartDate) {
    return true;
  }

  // Calculate the free return period
  const freeReturnPeriod = dayjs(coverageStartDate)
    .tz(storeTimeZone)
    .startOf('day')
    .add(dayEligibleForReturn, 'day');

  // Extract the offset from freeReturnPeriod
  const freeReturnPeriodOffset = freeReturnPeriod.utcOffset();

  // Set today's date with the same offset as freeReturnPeriod
  const today = dayjs().utcOffset(freeReturnPeriodOffset).startOf('day');

  // Compare using the same offset
  return (
    isHolidayReturnEligible(store, processedAt) ||
    today.isSameOrBefore(freeReturnPeriod)
  );
};

export const isWithinWarrantyCoverage = (
  coverageStartDate: string | null | undefined,
  isReebeloCare: boolean,
  storeTimeZone: string,
  duration: RC_DURATION,
) => {
  /*
   * No coverageStartDate date means the purchase was made recently
   * and therefore would return true
   */

  if (coverageStartDate === null || coverageStartDate === undefined) {
    return true;
  }
  const warrantyCoverageMonths = isReebeloCare ? Number(duration) : 12;
  const warrantyCoverageExpirationDate = dayjs(coverageStartDate)
    .tz(storeTimeZone)
    .startOf('date')
    .add(warrantyCoverageMonths, 'month');

  // An unreadable start date or duration can't prove cover. ("Invalid Date"
  // would sort after any YYYY-MM-DD below and read as covered.)
  if (!warrantyCoverageExpirationDate.isValid()) {
    return false;
  }

  const today = dayjs().tz(storeTimeZone).startOf('date');

  // Compared as calendar dates: adding months keeps the start's UTC offset,
  // so across a DST change the two midnights sit an hour apart and the last
  // day of cover read as already expired.
  return (
    today.format('YYYY-MM-DD') <=
    warrantyCoverageExpirationDate.format('YYYY-MM-DD')
  );
};

export const handleFilterReebeloCarePackages = (shipments: Shipment[]) => {
  const items: string[] = [];
  const cares: string[] = [];

  const reebeloCares = shipments.reduce(
    (reebeloCareItems: string[], shipment: Shipment) => {
      const currentReebeloCare = shipment.lineItems.reduce(
        (reebeloCareItem: string[], lineItem) => {
          if (lineItem.name.toLowerCase().includes('reebelocare')) {
            if (lineItem.isRefunded) {
              return reebeloCareItem;
            }

            if (lineItem.properties.length === 0) {
              cares.push(lineItem.name);
            }
            lineItem.properties.forEach((property) => {
              if (property.name.toLowerCase().includes('for')) {
                reebeloCareItem.push(property.value);
              }

              return reebeloCareItem;
            });
          } else {
            items.push(lineItem.name);
          }

          return reebeloCareItem;
        },
        [],
      );

      return [...reebeloCareItems, ...currentReebeloCare];
    },
    [],
  );

  if (reebeloCares.length === 0 && cares.length === items.length) {
    items.forEach((item) => reebeloCares.push(item));
  }

  return reebeloCares;
};

export const getDeliveryDate = (
  shipment: Shipment | undefined,
  processedAt: string,
) => {
  let deliveredDate = getDeliveryDateFromShipment(shipment);

  if (
    deliveredDate === null &&
    shipment?.tracking?.shipment_delivery_date !== null
  ) {
    deliveredDate = shipment?.tracking?.shipment_delivery_date || null;
  }

  if (deliveredDate === null) {
    deliveredDate = processedAt;
  }

  return deliveredDate;
};

function findMatchingLineItemFromShipments(
  shipments: Shipment[],
  lineItemName: string,
): ShipmentLineItem | undefined {
  if (!lineItemName) {
    return undefined;
  }

  return shipments
    .map((shipment) =>
      shipment.lineItems.find((lineItem) =>
        lineItem.properties
          .find((prop) => prop.name === 'For')
          ?.value?.toLowerCase()
          ?.includes(lineItemName),
      ),
    )
    .find((lineItem) => lineItem !== undefined);
}

export const lineItemWarrantyCoverageType = (
  shipments: Shipment[],
  shipment: Shipment | undefined,
  lineItem: ShipmentLineItem | undefined,
  storeTimeZone: string,
  processedAt: string,
) => {
  const reebeloCares = handleFilterReebeloCarePackages(shipments);

  // A mismatch between itemName and lineItem.name has been observed in some cases, so we're relying on both name and sku for matching
  const isReebeloCare = lineItem
    ? reebeloCares.some(
        (itemName) =>
          itemName.toLowerCase().includes(lineItem.name.toLowerCase()) ||
          itemName.includes(lineItem.sku),
      )
    : false;

  const reebeloCareLineItem = findMatchingLineItemFromShipments(
    shipments,
    lineItem?.name?.toLowerCase() ?? '',
  );

  const reebeloCaraDuration = (reebeloCareLineItem?.properties.find(
    (prop) => prop.name === 'Duration',
  )?.value ?? '24') as RC_DURATION;

  const deliveredDate = getDeliveryDate(shipment, processedAt);

  const isCoveredByWarranty = isWithinWarrantyCoverage(
    deliveredDate,
    isReebeloCare,
    storeTimeZone,
    reebeloCaraDuration,
  );

  const isCoveredByFreeReturn = isWithinFreeReturnPeriod(
    deliveredDate,
    storeTimeZone,
  );

  const careType = isReebeloCare ? 'reebelocare' : 'standard';

  const { value, unit } = getWarrantyCoverageTerms(
    isReebeloCare,
    lineItem,
    reebeloCaraDuration,
  );

  const warrantyCoverageExpirationDate = dayjs(deliveredDate)
    .tz(storeTimeZone)
    .startOf('date')
    .add(value, unit);

  /*
   * RT-3932
   * For products with ReebeloCare,
   * Non-accidental claims will be handled by Vendor Warranty in the 1st 12 month
   * then by the extened warranty between 12 ~ 24 month
   * Therefore we needed to know whether if the product is within the extended warranty
   * Default to false for non-reebelocare products
   */
  let isWithinExtendedWarranty = false;

  if (isReebeloCare) {
    const extendedWarrantyStartDate = dayjs(deliveredDate)
      .tz(storeTimeZone)
      .startOf('date')
      .add(1, 'year');

    const today = dayjs().tz(storeTimeZone).startOf('date');

    isWithinExtendedWarranty =
      today >= extendedWarrantyStartDate &&
      today <= warrantyCoverageExpirationDate;
  }
  const rcVersion = getWarrantyCoverageVersion(
    careType === 'reebelocare',
    processedAt,
    lineItem?.reebeloCare.isCovered
      ? lineItem?.reebeloCare.reebeloCareVendor
      : null,
  );

  return {
    careType,
    isCoverageValid: isCoveredByWarranty,
    isWithinExtendedWarranty,
    isCoveredByFreeReturn,
    expirationDate: dayjs(warrantyCoverageExpirationDate)
      .tz(storeTimeZone)
      .format('YYYY-MM-DD'),
    rcVersion,
  };
};
